import Button from 'components/Button';
import Error from 'components/form/Error';
import { FormRadio, FormRadioGroup } from 'components/form/FormRadioGroup';
import { FlexColumn, Grid } from 'components/form/generic';
import Info from 'components/form/Info';
import Input from 'components/form/Inputs/Input';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import Radio, { getRadioValue } from 'components/form/Inputs/Radio';
import Select from 'components/form/Inputs/Select';
import MENTOR_EXPECTATIONS, { mentorExpectations } from 'enums/MentorExpectations';
import Success from 'components/form/Success';
import Warning from 'components/form/Warning';
import Field from 'components/profileNavigation/Field';
import Head from 'components/profileNavigation/Head';
import EXPERTISE, { expertises, ExpertiseValues } from 'enums/Expertise';
import FORMATIONS, { formations, FormationValues } from 'enums/Formation';
import HOBBIES, { hobbies } from 'enums/Hobby';
import MENTOR_HEARD_ABOUT_MENTORSHIP, {
  mentorHeardAboutMentorships,
  MentorHeardAboutMentorshipValues,
} from 'enums/MentorHeardAboutMentorship';
import { MENTOR_PRIORITY_LEVELS } from 'enums/PriorityLevels';
import STUDY_LEVELS, { studyLevels, StudyLevelValues } from 'enums/StudyLevels';
import { FC } from 'react';
import {
  ColumnRadioContainer,
  Form,
  HeadContainer,
  PermissionsContainer,
  Subtitle,
} from './components';
import { Errors, initialErrors, State } from './types';

type Props = {
  title: string;
  subtitle: string | string[];
  state: State;
  loading: boolean;
  setState: React.Dispatch<React.SetStateAction<State>>;
  handleSubmit: React.FormEventHandler;
  errors: Errors;
  setErrors: React.Dispatch<React.SetStateAction<Errors>>;
  marginTop?: number | string;
  success?: boolean;
  invalid?: boolean;
};
const MentorFormEdit: FC<Props> = ({
  title,
  subtitle,
  state,
  loading,
  setState,
  handleSubmit,
  errors,
  setErrors,
  marginTop,
  success,
  invalid,
}) => {
  const handleChange: React.ChangeEventHandler<
    HTMLInputElement & HTMLSelectElement & HTMLTextAreaElement
  > = (e) => {
    let set, value;
    switch (e.target.name) {
      case 'jobSearch':
      case 'careerPlan':
      case 'studies':
      case 'entrepreneurship':
      case 'hobby':
        setErrors(initialErrors);
        return setState({
          ...state,
          [e.target.name]: getRadioValue(e.target.value),
        });

      case 'expertiseSelect':
        if (e.target.value !== 'DOMAIN') {
          set = new Set(state.expertises);
          set.add(e.target.value as ExpertiseValues);
          if (Array.from(set).length < 5) {
            return setState({
              ...state,
              expertises: Array.from(set),
            });
          }
          return setErrors({
            ...initialErrors,
            expertises: true,
          });
        }
        return;
      case 'expertiseCheck':
        setErrors(initialErrors);
        set = new Set(state.expertises);
        set.delete(e.target.value as ExpertiseValues);
        return setState({
          ...state,
          expertises: Array.from(set),
        });
      case 'formations':
        set = new Set(state.formations);
        if (e.target.checked) {
          set.add(e.target.value as FormationValues);
        } else {
          set.delete(e.target.value as FormationValues);
        }
        return setState({
          ...state,
          formations: Array.from(set),
        });
      case 'diploma':
        console.log(e.target.value);
        set = new Set(state.diploma);
        if (e.target.checked) {
          set.add(e.target.value as StudyLevelValues);
        } else {
          set.delete(e.target.value as StudyLevelValues);
        }
        return setState({
          ...state,
          diploma: Array.from(set),
        });
      case 'wantedMentorships':
        value = parseInt(e.target.value);
        if (value > 5 || value < 0) {
          return;
        }
        return setState({
          ...state,
          [e.target.name]: value,
        });

      case 'permissions':
        setErrors(initialErrors);
        return setState({
          ...state,
          [e.target.value]: e.target.checked,
        });

      case 'heardAboutMentorship':
        return setState({
          ...state,
          [e.target.name]:
            (e.target.value as MentorHeardAboutMentorshipValues) || null,
        });

      default:
        return setState({
          ...state,
          [e.target.name]: e.target.value,
        });
    }
  };

  return (
    <section style={{ maxWidth: '100%', marginTop }}>
      <HeadContainer>
        <Head title={title} subtitle={subtitle} />
        {errors.badRequest && (
          <Error>
            Le formulaire est invalide. Veuillez vérifier vos réponses.
          </Error>
        )}
        {success && <Success>Votre questionnaire a été enregistré.</Success>}
        {errors.general && (
          <Error>
            Une erreur s'est produite. Veuillez réessayer ultérieurement.
          </Error>
        )}
        {invalid && (
          <Warning>
            Le formulaire enregistré n'est pas valide. Vous devez enregistrer un
            formulaire valide pour passer à l'étape suivante.
          </Warning>
        )}
      </HeadContainer>
      <Form onSubmit={handleSubmit}>
        <Field label=" Vous pourriez aider un jeune à *">
          <FlexColumn>
            <FormRadioGroup
              title="Trouver un stage, une alternance, un emploi"
              name="jobSearch"
              value={state.jobSearch}
            >
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['PRIORITY']}
                id="PRIORITY"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['YES']}
                id="YES"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['NO']}
                id="NO"
                onChange={handleChange}
              />
            </FormRadioGroup>
            <FormRadioGroup
              title="Mieux définir son projet de formation ou projet professionnel"
              name="careerPlan"
              value={state.careerPlan}
            >
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['PRIORITY']}
                id="PRIORITY"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['YES']}
                id="YES"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['NO']}
                id="NO"
                onChange={handleChange}
              />
            </FormRadioGroup>
            <FormRadioGroup
              title="Réussir ses études"
              name="studies"
              value={state.studies}
            >
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['PRIORITY']}
                id="PRIORITY"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['YES']}
                id="YES"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['NO']}
                id="NO"
                onChange={handleChange}
              />
            </FormRadioGroup>
            <FormRadioGroup
              title="Créer ou développer une activité"
              name="entrepreneurship"
              value={state.entrepreneurship}
            >
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['PRIORITY']}
                id="PRIORITY"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['YES']}
                id="YES"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTOR_PRIORITY_LEVELS['NO']}
                id="NO"
                onChange={handleChange}
              />
            </FormRadioGroup>
          </FlexColumn>
        </Field>

        <Field
          label={
            <>
              Dans quels domaines pensez-vous pouvoir apporter des compétences
              ou un réseau ? *<Subtitle>4 domaines maximum</Subtitle>
            </>
          }
        >
          <ColumnRadioContainer>
            {errors.expertises && (
              <Error>
                Vous ne pouvez sélectionner que 4 domaines maximum. Si vous
                souhaitez changer un domaine, veuillez le décocher avant de
                choisir un autre domaine.
              </Error>
            )}
            <Select
              value="DOMAIN"
              name="expertiseSelect"
              onChange={handleChange}
            >
              <option value="DOMAIN">-- Sélectionnez un domaine --</option>
              {expertises.map((expertise, idx) => (
                <option key={idx} value={expertise}>
                  {EXPERTISE[expertise]}
                </option>
              ))}
            </Select>
            <Grid>
              {state.expertises.map((expertise, idx) => (
                <LabelledCheckbox
                  key={idx}
                  label={EXPERTISE[expertise]}
                  checked
                  onChange={handleChange}
                  name="expertiseCheck"
                  value={expertise}
                />
              ))}
            </Grid>
          </ColumnRadioContainer>
        </Field>

        <Field label="Quel a été votre parcours de formation ou quels parcours de formation connaissez-vous le mieux ? *">
          <Grid>
            {formations.map((formation, idx) => (
              <LabelledCheckbox
                key={idx}
                label={FORMATIONS[formation]}
                checked={state.formations.includes(formation)}
                onChange={handleChange}
                name="formations"
                value={formation}
              />
            ))}
          </Grid>
        </Field>
        <Field label="Vous vous sentirez plus à l'aise avec un jeune qui vise ou qui a obtenu dans son projet un niveau de diplôme *">
          <Grid>
            {studyLevels.map((studyLevel, idx) => (
              <LabelledCheckbox
                key={idx}
                label={STUDY_LEVELS[studyLevel]}
                checked={state.diploma.includes(studyLevel)}
                onChange={handleChange}
                value={studyLevel}
                name="diploma"
              />
            ))}
          </Grid>
        </Field>

        <Field label="Lorsque vous avez du temps libre, si vous avez le choix, vous choisissez plutôt *">
          <Grid>
            {hobbies.map((hobby, idx) => (
              <Radio
                key={idx}
                label={HOBBIES[hobby]}
                checked={state.hobby === hobby}
                onChange={handleChange}
                name="hobby"
                id={hobby}
              />
            ))}
          </Grid>
        </Field>

        <Field
          label={
            <>
              Combien de jeunes seriez-vous prêt à mentorer simultanément ?
              <Subtitle>(5 maximum)</Subtitle>
            </>
          }
        >
          <Input
            type="number"
            value={state.wantedMentorships}
            name="wantedMentorships"
            onChange={handleChange}
          />
        </Field>

        <Field label="Comment avez-vous entendu parler du mentorat?">
          <FlexColumn gap={0.5}>
            <Select
              value={state.heardAboutMentorship || ''}
              onChange={handleChange}
              name="heardAboutMentorship"
            >
              <option value="">-- Sélectionner une option --</option>
              {mentorHeardAboutMentorships.map((value, idx) => (
                <option key={idx} value={value}>
                  {MENTOR_HEARD_ABOUT_MENTORSHIP[value]}
                </option>
              ))}
            </Select>
            {state.heardAboutMentorship === 'OTHER' && (
              <Input
                value={state.heardAboutMentorshipText || ''}
                onChange={handleChange}
                name="heardAboutMentorshipText"
                placeholder="Veuillez préciser ici"
              />
            )}
          </FlexColumn>
        </Field>

        <Field label="Ce que j'attends du mentorat">
          <FlexColumn gap={0.5}>
            <Info>Ce champ pourra être vu par vos mentorés.</Info>
            {errors.expectations && (
              <Error>Veuillez sélectionner au moins une option.</Error>
            )}
            {mentorExpectations.map((expectation) => {
              const selected = (state.expectations || '').split(',').filter(Boolean);
              return (
                <LabelledCheckbox
                  key={expectation}
                  label={MENTOR_EXPECTATIONS[expectation]}
                  checked={selected.includes(expectation)}
                  onChange={(e) => {
                    const set = new Set(selected);
                    if (e.target.checked) {
                      set.add(expectation);
                    } else {
                      set.delete(expectation);
                    }
                    setErrors(initialErrors);
                    setState({
                      ...state,
                      expectations: Array.from(set).join(','),
                    });
                  }}
                />
              );
            })}
          </FlexColumn>
        </Field>

        <Field label="Partager mes informations avec le mentoré">
          <PermissionsContainer>
            {errors.permissions && <Error>Veuillez choisir une option.</Error>}
            <LabelledCheckbox
              label="J’autorise le partage de mon adresse email avec le mentoré"
              checked={!!state.sharingEmail}
              name="permissions"
              value="sharingEmail"
              onChange={handleChange}
            />
            <LabelledCheckbox
              label="J’autorise le partage de mon numéro de téléphone avec le mentoré"
              checked={!!state.sharingPhone}
              name="permissions"
              value="sharingPhone"
              onChange={handleChange}
            />
          </PermissionsContainer>
        </Field>

        <Button color="primary" loading={loading}>
          Valider le questionnaire
        </Button>
      </Form>
    </section>
  );
};

export default MentorFormEdit;
